import {
  MODAL_INNER_ROOT,
  SELECTORS,
} from 'chameleon/packages/blue-book-player/constants/selectors';
import {
  BUTTON_WIDTH,
  HALF_BUTTON_WIDTH,
  MOVING_MODAL_GAP,
} from 'chameleon/packages/blue-book-player/constants/modals';
import { KEY_CODES } from 'chameleon/packages/blue-book-player/constants/keyCodes';
import {
  areLearnosityNativeColumns,
  getFirstNodeTextPos,
  getItemByRef,
} from 'chameleon/packages/blue-book-player/utils';
import {
  ReferenceSheetVisibleSubjects,
  ReferenceSheetComingSoonSubjects,
} from 'chameleon/packages/blue-book-player/constants/subjects';

export const resetToDefaultPosition = (
  playerEl,
  modalWidth,
  defaultResetTo,
  callback,
) => {
  const innerContainer = playerEl?.querySelector(SELECTORS.INNER_CONTAINER);
  if (innerContainer) {
    const { top, width: innerContainerWidth } =
      innerContainer.getBoundingClientRect();
    callback({
      x:
        defaultResetTo?.x != null
          ? defaultResetTo?.x
          : innerContainerWidth - modalWidth,
      y: defaultResetTo?.y != null ? defaultResetTo?.y : top,
    });
  }
};

export const calculateBounds = (modalWidth) => ({
  left: 0 - modalWidth / 2 + HALF_BUTTON_WIDTH,
  bottom: window.innerHeight - BUTTON_WIDTH,
  right: window.innerWidth - modalWidth / 2 - HALF_BUTTON_WIDTH,
});

export const getCalculateWidth = (modalWidth) => {
  if (modalWidth > window.innerWidth) {
    return window.innerWidth;
  }

  return modalWidth;
};

export const manualCalculatePos = (keyCode, currentPos, bounds) => {
  let newY;
  let newX;
  switch (keyCode) {
    case KEY_CODES.upArrow:
      newY = currentPos.y - MOVING_MODAL_GAP;
      return {
        y: newY < 0 ? bounds.top : newY,
      };
    case KEY_CODES.downArrow:
      newY = currentPos.y + MOVING_MODAL_GAP;
      return {
        y: newY > bounds.bottom ? bounds.bottom : newY,
      };
    case KEY_CODES.leftArrow:
      newX = currentPos.x - MOVING_MODAL_GAP;
      return {
        x: newX < bounds.left ? bounds.left : newX,
      };
    case KEY_CODES.rightArrow:
      newX = currentPos.x + MOVING_MODAL_GAP;
      return {
        x: newX > bounds.right ? bounds.right : newX,
      };
  }
};

const calculateLimitY = (y, defaultY, topGap) => {
  const newY = y - topGap;
  return newY > 0 ? newY : defaultY;
};

export const calculateDefaults = (
  questionReference,
  playerEl,
  blackListedClasses,
  topGap,
) => {
  let defaults;
  const innerWidth = parseInt(window.innerWidth, 10);
  if (questionReference) {
    const questionEl = getItemByRef(playerEl, questionReference);
    if (!questionEl) return {};
    SELECTORS.TWO_COLUMNS.some((selectors) => {
      const firstColumnEl = questionEl?.querySelector(selectors[0]);
      const secondColumnEl = questionEl?.querySelector(selectors[1]);

      if (firstColumnEl && secondColumnEl) {
        const questionWith2NativeCol = areLearnosityNativeColumns(
          firstColumnEl,
          secondColumnEl,
        );
        const pos = getFirstNodeTextPos(
          firstColumnEl.querySelector('.lrn_widget'),
          blackListedClasses,
        );
        const SCROLL_WIDTH_GAP = questionWith2NativeCol ? 0 : 4;
        const halfScreenWidth = innerWidth / 2;
        const fixedWidth = halfScreenWidth - SCROLL_WIDTH_GAP;
        defaults = {
          hasTwoColumns: true,
          width: fixedWidth,
          expandedWidth: innerWidth,
          collapsedWidth: fixedWidth,
          position: {
            x: 0,
            y: pos ? calculateLimitY(pos.y, 50, topGap) : 50,
          },
        };

        return true;
      }
    });

    if (defaults) {
      return defaults;
    }

    const pos = getFirstNodeTextPos(
      questionEl.querySelector('.lrn_widget'),
      blackListedClasses,
    );
    return {
      hasTwoColumns: false,
      width: innerWidth,
      expandedWidth: innerWidth,
      collapsedWidth: (innerWidth / 2).toFixed(),
      position: {
        x: 0,
        y: pos ? calculateLimitY(pos.y, 86, topGap) : 86,
      },
    };
  }

  return {};
};

export const isHighlightRestricted = (node) => {
  if (!node) return false;

  const nonHighlightableElements = [
    SELECTORS.OPTION_ANSWER_LIST,
    SELECTORS.CROSSOUT_CONTAINER,
    MODAL_INNER_ROOT,
    SELECTORS.REVIEW_PAGE,
    SELECTORS.FRQ_CONTAINER_EDITOR,
    SELECTORS.SHARED_PASSAGE_TITLE,
  ];

  return nonHighlightableElements.some((selector) => node.closest(selector));
};
export const areInsideInnerContainer = (startNode, endNode) => {
  return (
    startNode?.closest(SELECTORS.INNER_CONTAINER) &&
    endNode?.closest(SELECTORS.INNER_CONTAINER)
  );
};

export const isReferenceSheetMenuVisible = (subject) =>
  [
    ...ReferenceSheetVisibleSubjects,
    ...ReferenceSheetComingSoonSubjects,
  ].includes(subject);

const setItemIsLoaded = (
  questionEl,
  isFirstItemLoading,
  onFirstItemLoadingCallback,
) => {
  questionEl?.classList?.add('item-is-loaded');
  isFirstItemLoading.current = false;
  if (typeof onFirstItemLoadingCallback === 'function') {
    onFirstItemLoadingCallback();
  }
};

export const runSetItemIsLoaded = (
  questionEl,
  isFirstItemLoading,
  onFirstItemLoadingCallback,
) => {
  if (!isFirstItemLoading.current) {
    setItemIsLoaded(questionEl, isFirstItemLoading, onFirstItemLoadingCallback);
  } else {
    setTimeout(() => {
      setItemIsLoaded(
        questionEl,
        isFirstItemLoading,
        onFirstItemLoadingCallback,
      );
    }, 1000);
  }
};

export const addTwoColumnsWrapperClass = (columnElement) => {
  columnElement?.parentElement?.classList?.add(
    SELECTORS.TWO_COLUMNS_WRAPPER_CLASS,
  );
};

export const addClassesForHeightDivs = (questionElement) => {
  const heightPassage = questionElement?.querySelector('div[style*="height"]');
  if (
    heightPassage?.parentElement?.classList.contains('col-xs-6') ||
    heightPassage?.parentElement?.classList.contains('col-sm-6')
  ) {
    const smallColumnsXs = questionElement?.querySelectorAll('.col-xs-6');
    const smallColumnsSm = questionElement?.querySelectorAll('.col-sm-6');
    smallColumnsXs?.forEach((column) => {
      column
        .querySelector('div[style*="height"]')
        ?.classList?.add('!max-h-full');
    });
    smallColumnsSm?.forEach((column) => {
      column
        .querySelector('div[style*="height"]')
        ?.classList?.add('!max-h-full');
    });
  } else {
    heightPassage?.classList?.add('height-passage');
  }
};

export const addOverflowInRow = (questionElement) => {
  const isHeightElement = !!questionElement?.querySelector(
    ':scope > div[style*="height"]',
  );
  const hasLrnOverviewElement =
    !!questionElement?.querySelector('.lrn-overview');

  if (isHeightElement || hasLrnOverviewElement) return;
  const rowElement = questionElement?.querySelector('.row');
  rowElement?.classList?.add('custom-overflow');
};

export const addHeightAutoForHeightDiv = (playerEl, reference) => {
  const questionElement = getItemByRef(playerEl, reference);

  const smallColumns = questionElement?.querySelectorAll('.col-xs-6');
  smallColumns?.forEach((column) => {
    column?.querySelector('div[style*="height"]')?.classList?.add('!h-auto');
  });
};

export const addPaddingToColumns = (
  passageContainerElement,
  questionContainerElement,
) => {
  if (!passageContainerElement?.classList.contains('lrn_audioplayer')) {
    const allChildren =
      passageContainerElement?.querySelectorAll(':scope > div');
    allChildren?.forEach((child) => {
      child?.classList?.add('!px-16');
    });
  }
  const allChildren =
    questionContainerElement?.querySelectorAll(':scope > div');
  allChildren?.forEach((child) => {
    child?.classList?.add('!px-16');
  });
};

export const addPaddingToColumn = (playerEl, reference) => {
  const questionElement = getItemByRef(playerEl, reference);
  const smallColumns = questionElement?.querySelectorAll('.col-xs-6');
  smallColumns?.forEach((column) => {
    column?.querySelector('div')?.classList.add('!px-16');
  });

  const questionElements = questionElement?.querySelectorAll(
    '.col-xs-12 [data-lrn-widget-type="question"]',
  );
  questionElements?.forEach((element) => {
    const divChild = element?.querySelector('div');
    if (!divChild?.classList?.contains('lrn_longtextV2')) {
      divChild?.classList?.add('!px-16');
    }
  });
};

const addWidthClassToChildren = (element) => {
  const firstDivElements = element?.querySelectorAll(':scope > div');
  firstDivElements?.forEach((divElement) => {
    const allContentElements = divElement?.querySelectorAll(':scope > *');
    allContentElements?.forEach((contentElement) => {
      contentElement?.classList?.add('custom-content-width');
    });
  });
};

export const addWidthClassToColumns = (
  passageContainerElement,
  questionContainerElement,
) => {
  if (passageContainerElement) {
    if (passageContainerElement.dataset.lrnWidgetType === 'feature') {
      addWidthClassToChildren(passageContainerElement);
    } else {
      const featurePassageElements = passageContainerElement.querySelectorAll(
        '[data-lrn-widget-type="feature"]',
      );
      featurePassageElements?.forEach(addWidthClassToChildren);
    }
  }
  if (questionContainerElement) {
    if (questionContainerElement.dataset.lrnWidgetType === 'question') {
      addWidthClassToChildren(questionContainerElement);
    } else {
      const questionElements = questionContainerElement.querySelectorAll(
        '[data-lrn-widget-type="question"]',
      );
      questionElements?.forEach(addWidthClassToChildren);
    }
  }
};

export const addWidthClassToColumn = (questionElement) => {
  const questionElements = questionElement?.querySelectorAll(
    '[data-lrn-widget-type="question"]',
  );
  questionElements?.forEach(addWidthClassToChildren);

  const smallColumns = questionElement?.querySelectorAll('.col-xs-6');
  if (smallColumns?.length > 0) {
    smallColumns?.forEach(addWidthClassToChildren);
    return;
  }

  const featurePassageElements = questionElement?.querySelectorAll(
    '[data-lrn-widget-type="feature"]',
  );
  featurePassageElements?.forEach(addWidthClassToChildren);
};

export const getAnchorElements = (questionElement) => {
  return questionElement?.querySelectorAll(SELECTORS.ANCHOR_LINK) ?? [];
};
